/**
 * Professional Email Template Manager
 * Handles template storage, import/export, and management operations
 */

const STORAGE_KEY = 'email_templates';
const MAX_TEMPLATES = 100;

/**
 * Template data structure
 * @typedef {Object} EmailTemplate
 * @property {string} id - Unique template ID
 * @property {string} name - Template name
 * @property {string} description - Template description
 * @property {string} mjml - MJML source code
 * @property {string} html - Compiled HTML
 * @property {number} createdAt - Creation timestamp
 * @property {number} modifiedAt - Last modified timestamp
 * @property {string} category - Template category
 * @property {string[]} tags - Template tags
 */

class TemplateManager {
  /**
   * Get all saved templates
   * @returns {EmailTemplate[]}
   */
  static getAllTemplates() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading templates:', error);
      return [];
    }
  }

  /**
   * Save template to storage
   * @param {EmailTemplate} template
   * @returns {boolean} Success status
   */
  static saveTemplate(template) {
    try {
      const templates = this.getAllTemplates();
      
      // Check if template already exists
      const existingIndex = templates.findIndex(t => t.id === template.id);
      
      if (existingIndex >= 0) {
        // Update existing template
        templates[existingIndex] = {
          ...template,
          modifiedAt: Date.now()
        };
      } else {
        // Add new template
        if (templates.length >= MAX_TEMPLATES) {
          throw new Error(`Maximum ${MAX_TEMPLATES} templates allowed`);
        }
        templates.push({
          ...template,
          id: template.id || this.generateId(),
          createdAt: Date.now(),
          modifiedAt: Date.now()
        });
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
      return true;
    } catch (error) {
      console.error('Error saving template:', error);
      return false;
    }
  }

  /**
   * Load template by ID
   * @param {string} id
   * @returns {EmailTemplate|null}
   */
  static loadTemplate(id) {
    const templates = this.getAllTemplates();
    return templates.find(t => t.id === id) || null;
  }

  /**
   * Delete template by ID
   * @param {string} id
   * @returns {boolean} Success status
   */
  static deleteTemplate(id) {
    try {
      const templates = this.getAllTemplates();
      const filtered = templates.filter(t => t.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Error deleting template:', error);
      return false;
    }
  }

  /**
   * Duplicate template
   * @param {string} id
   * @returns {EmailTemplate|null}
   */
  static duplicateTemplate(id) {
    const template = this.loadTemplate(id);
    if (!template) return null;

    const duplicate = {
      ...template,
      id: this.generateId(),
      name: `${template.name} (Copy)`,
      createdAt: Date.now(),
      modifiedAt: Date.now()
    };

    this.saveTemplate(duplicate);
    return duplicate;
  }

  /**
   * Export template as JSON
   * @param {EmailTemplate} template
   * @returns {string} JSON string
   */
  static exportAsJSON(template) {
    return JSON.stringify(template, null, 2);
  }

  /**
   * Export template as MJML
   * @param {EmailTemplate} template
   * @returns {string} MJML string
   */
  static exportAsMJML(template) {
    return template.mjml;
  }

  /**
   * Export template as HTML
   * @param {EmailTemplate} template
   * @returns {string} HTML string
   */
  static exportAsHTML(template) {
    return template.html;
  }

  /**
   * Import template from JSON file
   * @param {File} file
   * @returns {Promise<EmailTemplate>}
   */
  static async importFromJSON(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const template = JSON.parse(e.target.result);
          
          // Validate template structure
          if (!template.mjml) {
            throw new Error('Invalid template: missing MJML content');
          }
          
          // Assign new ID to avoid conflicts
          template.id = this.generateId();
          template.createdAt = Date.now();
          template.modifiedAt = Date.now();
          
          resolve(template);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Import template from MJML file
   * @param {File} file
   * @returns {Promise<EmailTemplate>}
   */
  static async importFromMJML(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const mjml = e.target.result;
          
          // Basic MJML validation
          if (!mjml.includes('<mjml>') || !mjml.includes('</mjml>')) {
            throw new Error('Invalid MJML: missing <mjml> tags');
          }
          
          const template = {
            id: this.generateId(),
            name: file.name.replace(/\.mjml$/i, ''),
            description: 'Imported from MJML file',
            mjml: mjml,
            html: '', // Will be compiled by GrapesJS
            createdAt: Date.now(),
            modifiedAt: Date.now(),
            category: 'imported',
            tags: ['imported']
          };
          
          resolve(template);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Validate file type
   * @param {File} file
   * @param {string[]} allowedTypes
   * @returns {boolean}
   */
  static validateFileType(file, allowedTypes = ['.json', '.mjml', '.html']) {
    const fileName = file.name.toLowerCase();
    return allowedTypes.some(type => fileName.endsWith(type));
  }

  /**
   * Validate file size
   * @param {File} file
   * @param {number} maxSizeMB
   * @returns {boolean}
   */
  static validateFileSize(file, maxSizeMB = 5) {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  /**
   * Generate unique ID
   * @returns {string}
   */
  static generateId() {
    return `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Search templates
   * @param {string} query
   * @returns {EmailTemplate[]}
   */
  static searchTemplates(query) {
    const templates = this.getAllTemplates();
    const lowerQuery = query.toLowerCase();
    
    return templates.filter(t => 
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
    );
  }

  /**
   * Filter templates by category
   * @param {string} category
   * @returns {EmailTemplate[]}
   */
  static filterByCategory(category) {
    const templates = this.getAllTemplates();
    return templates.filter(t => t.category === category);
  }

  /**
   * Get template statistics
   * @returns {Object}
   */
  static getStats() {
    const templates = this.getAllTemplates();
    return {
      total: templates.length,
      categories: [...new Set(templates.map(t => t.category))],
      tags: [...new Set(templates.flatMap(t => t.tags))],
      maxAllowed: MAX_TEMPLATES,
      remaining: MAX_TEMPLATES - templates.length
    };
  }

  /**
   * Clear all templates (with safety check)
   * @param {boolean} confirm
   * @returns {boolean}
   */
  static clearAll(confirm = false) {
    if (!confirm) {
      return false;
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing templates:', error);
      return false;
    }
  }

  /**
   * Download file helper
   * @param {string} content
   * @param {string} filename
   * @param {string} mimeType
   */
  static downloadFile(content, filename, mimeType = 'text/plain') {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export default TemplateManager;
